/**
 * Group opacity for the canvas scene (WI #5531).
 *
 * SVG dims a filter-shadowed item with `opacity: 0.2` on its wrapping `<g>`: the
 * whole group is rasterised at full opacity first, then the flattened result is
 * composited once at 0.2. Overlapping sub-shapes (a stroke over its fill, an icon
 * whose strokes cross, a number over the hexagon) therefore do NOT darken each
 * other.
 *
 * Setting `ctx.globalAlpha = 0.2` and drawing each sub-shape separately does the
 * opposite — every shape composites against the translucent pixels already laid
 * down, so overlaps accumulate and look darker than the rest of the group. This
 * helper restores the SVG behaviour: it runs `draw` against an offscreen buffer
 * that mirrors the target's size + transform, then blits that buffer onto the
 * target a single time at `alpha`.
 *
 * Cost is one full-canvas `drawImage` per call, so callers should BATCH — pass a
 * `draw` that renders every dimmed item of a painter in one go, not one call per
 * item. Dimming only happens while a map filter / milestone-overlay is active, so
 * the unfiltered hot path pays nothing (callers skip this when nothing is dimmed).
 */

// One reusable backing buffer, grown to match the largest target seen. Module
// scope keeps it across frames (a fresh canvas per frame would thrash the GC).
let scratch: HTMLCanvasElement | null = null;
let scratchCtx: CanvasRenderingContext2D | null = null;

// When the scene is painted to the vector PDF export ctx (svgcanvas), the
// scratch-canvas flatten below can't run — that ctx has no backing
// `HTMLCanvasElement` (`ctx.canvas`) to size against or `drawImage`-blit. Export
// renders the static map only (no dimming filter is active), so we degrade to a
// plain `globalAlpha` group: correct for the common case, and the rare overlap
// darkening of a dimmed item is irrelevant to a full-state export.
let exportMode = false;

/** Toggle the vector-export degrade path (set by `export/renderMapSvg.ts`). */
export function setGroupOpacityExportMode(on: boolean): void {
  exportMode = on;
}

export function withGroupOpacity(
  ctx: CanvasRenderingContext2D,
  alpha: number,
  draw: (g: CanvasRenderingContext2D) => void,
): void {
  if (alpha >= 1) {
    draw(ctx);

    return;
  }
  if (exportMode) {
    ctx.save();
    ctx.globalAlpha = alpha;
    draw(ctx);
    ctx.restore();

    return;
  }
  const { canvas } = ctx;
  if (!scratch || !scratchCtx) {
    scratch = document.createElement('canvas');
    scratchCtx = scratch.getContext('2d');
  }
  if (!scratchCtx) {
    // No offscreen context available — fall back to direct draw (overlaps may
    // darken, but the scene still renders).
    draw(ctx);

    return;
  }
  if (scratch.width !== canvas.width || scratch.height !== canvas.height) {
    scratch.width = canvas.width;
    scratch.height = canvas.height;
  }

  const g = scratchCtx;
  // Clear in device space, then mirror the target's transform so `draw` uses the
  // exact same scene coordinates it would on the real layer.
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, scratch.width, scratch.height);
  g.setTransform(ctx.getTransform());
  g.save();
  draw(g);
  g.restore();

  // Composite the flattened group once, in device space, at the group alpha.
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = alpha;
  ctx.drawImage(scratch, 0, 0);
  ctx.restore();
}
